<template>
  <v-container class="fill-height">
    <v-row justify="center" align-items="center">
      <v-col cols="12" class="d-flex justify-center">
        <v-card class="w-100" style="max-width: 700px">
          <Form v-if="!submitSuccess" @submit="submit">
            <v-card-title class="text-h6 d-flex justify-space-between align-center">
              <span>Contact Us</span>
              <v-btn
                variant="text"
                size="small"
                color="primary"
                to="/"
                class="text-caption"
              >
                <v-icon start size="small">mdi-arrow-left</v-icon>
                Back to Home
              </v-btn>
            </v-card-title>
            <v-divider/>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <Field 
                    v-slot="{ field, errors }" 
                    name="name" 
                    :rules="nameResolver"
                  >
                    <v-text-field 
                      v-bind="field"
                      label="Name"
                      :error-messages="errors"
                      :disabled="isLoading"
                    />
                  </Field>
                </v-col>
                <v-col cols="12">
                  <Field 
                    v-slot="{ field, errors }" 
                    name="subject" 
                    :rules="subjectResolver"
                  >
                    <v-text-field 
                      v-bind="field"
                      label="Subject"
                      :error-messages="errors"
                      :disabled="isLoading"
                    />
                  </Field>
                </v-col>
                <v-col cols="12">
                  <Field 
                    v-slot="{ field, errors }" 
                    name="email" 
                    :rules="emailResolver"
                  >
                    <v-text-field 
                      v-bind="field"
                      label="Email"
                      type="email"
                      :error-messages="errors"
                      :disabled="isLoading"
                    />
                  </Field>
                </v-col>
                <v-col cols="12">
                  <Field 
                    v-slot="{ field, errors }" 
                    name="message" 
                    :rules="messageResolver"
                  >
                    <v-textarea 
                      v-bind="field"
                      label="Message"
                      rows="5"
                      :error-messages="errors"
                      :disabled="isLoading"
                    />
                  </Field>
                </v-col>
                <v-col cols="12" class="d-flex justify-center">
                  <div class="text-center">
                    <NuxtTurnstile 
                      v-model="turnstileToken"
                      :class="{ 'turnstile-error': turnstileError }"
                    />
                    <v-alert 
                      v-if="turnstileError"
                      type="error"
                      density="compact"
                      class="mt-2"
                    >
                      Please complete the security verification
                    </v-alert>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn 
                type="submit"
                :loading="isLoading"
                :disabled="isLoading"
              >
                {{ isLoading ? 'Sending...' : 'Send Message' }}
              </v-btn>
            </v-card-actions>
          </Form>
          
          <div v-else>
            <v-card-title class="text-h6 d-flex justify-space-between align-center">
              <span>Message Sent</span>
              <v-btn
                variant="text"
                size="small"
                color="primary"
                to="/"
                class="text-caption"
              >
                <v-icon start size="small">mdi-arrow-left</v-icon>
                Back to Home
              </v-btn>
            </v-card-title>
            <v-divider/>
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
              <h3 class="text-h6 mb-4">Thank you for contacting us!</h3>
              <p class="text-body-1 mb-4">Your message has been sent successfully. We will get back to you soon!</p>
              <v-btn
                variant="outlined"
                color="primary"
                @click="resetForm"
              >
                Send Another Message
              </v-btn>
            </v-card-text>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { Form, Field } from 'vee-validate';
import { ref, onMounted, watch } from 'vue';
import { post } from '~/composables/useApi';
import { useValidation } from '~/composables/useValidation';
import { useAppTheme } from '~/composables/useTheme';
import type { SubmissionHandler } from 'vee-validate';

interface ContactFormValues {
  name: string;
  subject: string;
  email: string;
  message: string;
}

const isLoading = ref(false);
const turnstileError = ref(false);
const turnstileToken = ref<string | null>(null);
const submitSuccess = ref(false);

// Initialize theme for public page
const { initializeLoginTheme } = useAppTheme();

// Use unified validation rules
const { rules } = useValidation();

const nameResolver = rules.required;
const subjectResolver = rules.required;
const emailResolver = rules.email;
const messageResolver = rules.required;

// Watch for turnstile token changes
watch(turnstileToken, (newToken) => {
  if (newToken) {
    turnstileError.value = false;
  }
});

// Submit function
const submit: SubmissionHandler = async (values) => {
  const formValues = values as ContactFormValues;
  
  // Validate Turnstile
  if (!turnstileToken.value) {
    turnstileError.value = true;
    return;
  }
  
  try {
    isLoading.value = true;
    
    const contactData = {
      name: formValues.name,
      subject: formValues.subject,
      email: formValues.email,
      message: formValues.message,
      turnstileToken: turnstileToken.value
    };

    await post('public/contact', contactData);
    
    // Set success state to hide form and show success message
    submitSuccess.value = true;
  } finally {
    isLoading.value = false;
  }
};

// Reset form to allow sending another message
const resetForm = () => {
  submitSuccess.value = false;
  turnstileToken.value = null;
  turnstileError.value = false;
};

// Initialize on mount
onMounted(() => {
  initializeLoginTheme();
});

</script>

<style scoped>
.turnstile-error {
  border: 2px solid rgb(var(--v-theme-error));
  border-radius: 0.25rem;
  padding: 0.125rem;
}
</style>